import { useEffect } from "react"
import { useBlocker } from "react-router-dom"

/**
 * Предупреждение о несохранённых данных при уходе со страницы.
 *
 * В react-router 5 это был компонент `<Prompt when message />`, который без
 * собственного `getUserConfirmation` показывал системный `window.confirm`.
 * В шестой версии компонента нет — блокировку перехода даёт `useBlocker`
 * у data-роутера (он поднят в `App`), а вопрос мы задаём сами.
 *
 * Почему не готовый `unstable_usePrompt`: он отменяет переход (`reset`), если
 * блокировка выключилась уже после того, как переход заблокировали. А именно
 * так закрываются карточки после сохранения — `resetForm()` и переход идут
 * подряд, React 18 склеивает их в один рендер, и на момент перехода форма с
 * точки зрения React ещё «грязная». Переход блокировался, а к моменту разбора
 * блокировки предупреждать было уже не о чем — окно просто не закрывалось.
 * Поэтому в такой ситуации переход пропускаем, а не отменяем.
 *
 * Блокировщик поднят всё время жизни компонента: роутер учитывает только
 * последний зарегистрированный и ругается на второй, но маршрутный `Prompt`
 * смонтирован не больше одного за раз — это карточки справочников и подобные
 * им формы, каждая на своей странице.
 */
export const Prompt = ({ when, message }: PromptProps) => {
    const blocker = useBlocker(when)

    useEffect(() => {
        if (blocker.state !== "blocked") {
            return
        }

        // форма успела очиститься, пока переход ждал решения — вопрос ни к чему
        if (!when) {
            blocker.proceed()
            return
        }

        if (window.confirm(message)) {
            // таймаут — против гонки роутера с откатом истории на переходах «назад»
            setTimeout(blocker.proceed, 0)
        } else {
            blocker.reset()
        }
    }, [blocker, when, message])

    return null
}

interface PromptProps {
    /** блокировать ли уход со страницы */
    when: boolean
    /** текст вопроса */
    message: string
}
